import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

// Template50: Menú/Plan de viaje de Tokio
// Copia DIRECTA del componente Page2.jsx de guia-tokio, adaptado para usar las props del sistema guia
const Template50 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onNavigateToPage,
	pages = [],
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [logoError, setLogoError] = useState(false);
	const [failedMenuImageUrls, setFailedMenuImageUrls] = useState({});
	const markMenuImageFailed = (url) => {
		setFailedMenuImageUrls((prev) => ({ ...prev, [url]: true }));
	};

	// Datos hardcodeados de Tokio (valores por defecto)
	const defaultDias = [
		{
			image: "/images/japon/asakusa-menu.png",
			titulo: "Asakusa, Ueno y Akihabara",
			tituloMovil: "Asakusa, Ueno\ny Akihabara",
		},
		{
			image: "/images/japon/shibuya-menu.png",
			titulo: "Harajuku y Shibuya",
			tituloMovil: "Harajuku\ny Shibuya",
		},
		{
			image: "/images/japon/kamakura-menu.png",
			titulo: "Excursión a Kamakura",
			tituloMovil: "Excursión a\nKamakura",
		},
		{
			image: "/images/japon/tokio-menu.png",
			titulo: "Palacio Imperial, Ginza y Odaiba",
			tituloMovil: "Palacio Imperial,\nGinza y Odaiba",
		},
		{
			image: "/images/japon/shinjuku-menu.png",
			titulo: "Shinjuku y Torre de Tokio",
			tituloMovil: "Shinjuku\ny Torre de Tokio",
		},
		{
			image: "/images/japon/fuji-menu.png",
			titulo: "Día libre o excursión",
			tituloMovil: "Día libre\no excursión",
		},
	];

	const defaultExtras = [
		{
			image: "/images/japon/metro-menu.png",
			titulo: "Metro",
		},
		{
			image: "/images/japon/jrpass-menu.png",
			titulo: "JRPass",
		},
		{
			image: "/images/japon/tienda-menu.png",
			titulo: "Tiendas originales",
		},
		{
			image: "/images/airport-sticker.png",
			titulo: "Aeropuertos",
		},
		{
			image: "/images/consejos-stickers.png",
			titulo: "Consejos útiles",
		},
	];

	// Mapeo de días a templateId (para encontrar la página correspondiente)
	const diasTemplateMapping = [51, 54, 56, 57, 59, 61]; // Asakusa, Harajuku, Kamakura, Ginza, Shinjuku, Día libre

	// Mapeo de extras a templateId
	const extrasTemplateMapping = [37, 38, 39, 40, 41]; // Metro, JRPass, Tiendas, Aeropuertos, Consejos

	// Función para encontrar una página por templateId
	const findPageByTemplateId = (templateId) => {
		return pages.find(
			(p) => p.templateId === templateId || p.data?.templateId === templateId,
		);
	};

	// Función para obtener el pageId de una página
	const getPageId = (page) => {
		return page?.id || page?.data?.id;
	};

	// Si hay datos en page.data, usarlos; si no, usar los valores por defecto
	const pageData = page.data || {};
	const diasRaw =
		pageData.dias && pageData.dias.length > 0 ? pageData.dias : defaultDias;
	const extrasRaw =
		pageData.extras && pageData.extras.length > 0
			? pageData.extras
			: defaultExtras;

	// Enriquecer días con pageId basado en templateId
	const dias = diasRaw.map((dia, index) => {
		const templateId = diasTemplateMapping[index];
		const targetPage = templateId ? findPageByTemplateId(templateId) : null;
		const pageId = targetPage ? getPageId(targetPage) : null;
		return {
			...dia,
			pageId: dia.pageId || pageId, // Usar pageId del dato si existe, sino buscar por templateId
		};
	});

	// Enriquecer extras con pageId basado en templateId
	const extras = extrasRaw
		.map((extra, index) => {
			// Filtrar Kofi
			if (extra.titulo === "Kofi" || extra.image?.includes("kofi")) {
				return null; // Retornar null para filtrarlo después
			}
			const templateId =
				index >= 0 && index < extrasTemplateMapping.length
					? extrasTemplateMapping[index]
					: null;
			const targetPage = templateId ? findPageByTemplateId(templateId) : null;
			const pageId = targetPage ? getPageId(targetPage) : null;
			return {
				...extra,
				pageId: extra.pageId || pageId, // Usar pageId del dato si existe, sino buscar por templateId
			};
		})
		.filter(Boolean); // Filtrar los nulls

	const extrasToShow = extras;

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	// Lógica especial para la guía de NY: header #6C5CE7 en primera página, #59c3d8 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const isNYGuide = headerBackgroundColor === "#6C5CE7";
	let actualHeaderColor = headerBackgroundColor || "#24221a";
	if (isTokioGuide && pageOrder > 0) {
		actualHeaderColor = "#e24217";
	} else if (isNYGuide && pageOrder > 0) {
		actualHeaderColor = "#59c3d8";
	}

	const headerText = page.texts?.[0]?.content || "Plan de viaje";
	const logoImage =
		page.canvasImages?.find((img) => img.id === "logo")?.imageUrl ||
		"/images/logo.png";

	// Función para navegar
	const handleNavigate = (pageId) => {
		if (onNavigateToPage && pageId) {
			onNavigateToPage(pageId);
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#d2ebf0",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 800,
					position: "sticky",
					top: 0,
					zIndex: 1,
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				}}
			>
				{headerText}
				{logoImage && !logoError && (
					<Box
						component="img"
						src={logoImage}
						alt=""
						onError={() => setLogoError(true)}
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* CONTENIDO */}
			<Box data-block-id="menu" sx={{ padding: isSmallScreen ? "10px 10px" : "20px 16px" }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: isSmallScreen ? "10px" : "20px",
						flexWrap: "nowrap",
					}}
				>
					{dias.map((dia, index) => (
						<Box
							key={index}
							onClick={() => {
								if (dia.pageId) {
									handleNavigate(dia.pageId);
								}
							}}
							sx={{
								borderRadius: "12px",
								padding: 0,
								display: "flex",
								alignItems: "center",
								gap: isSmallScreen ? "5px" : "10px",
								position: "relative",
								minHeight: 30,
								width: isSmallScreen ? "100%" : "auto",
								cursor: dia.pageId ? "pointer" : "default",
								transition: "transform 0.3s ease",
								marginLeft: isSmallScreen ? 0 : "20px",
								"&:hover": dia.pageId
									? {
											transform: "translateY(-5px)",
											"& .box": {
												boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
											},
										}
									: {},
							}}
						>
							{dia.image && !failedMenuImageUrls[dia.image] && (
								<Box
									component="img"
									src={dia.image}
									alt=""
									onError={() => markMenuImageFailed(dia.image)}
									sx={{
										width: isSmallScreen ? 35 : 45,
										zIndex: 100,
										position: "absolute",
										left: isSmallScreen ? 8 : -20,
									}}
								/>
							)}
							<Box
								className="box"
								sx={{
									backgroundColor: "#fff",
									borderRadius: "12px",
									padding: "18px 16px",
									display: "flex",
									alignItems: "center",
									gap: isSmallScreen ? 0 : "10px",
									position: "relative",
									minHeight: 30,
									width: "100%",
									marginLeft: 0,
									textAlign: "left",
									height: isSmallScreen ? "100%" : "auto",
									flex: 1,
									transition: "box-shadow 0.3s ease",
									paddingLeft: isSmallScreen ? "38px" : "16px",
								}}
							>
								<Box
									sx={{
										fontSize: "13px !important",
										fontWeight: 600,
										color: "#333",
										marginLeft: isSmallScreen ? "10px" : "20px",
										fontFamily: `${
											fontFamily || "'Poppins', sans-serif"
										} !important`,
										whiteSpace: "pre-line",
									}}
								>
									Día {index + 1}:{" "}
									{isSmallScreen && dia.tituloMovil
										? dia.tituloMovil.replace(/\n/g, "\n")
										: dia.titulo}
								</Box>
							</Box>
						</Box>
					))}
				</Box>

				{/* SEPARADOR */}
				<Box
					component="h3"
					sx={{
						fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
						fontSize: 12,
						fontWeight: 700,
						margin: 0,
						textTransform: "uppercase",
						marginTop: 2,
						marginBottom: 1,
					}}
				>
					Información útil
				</Box>

				{/* INFORMACIÓN ÚTIL */}
				<Box
					sx={{
						display: "flex",
						gap: "8px",
						flexWrap: isSmallScreen ? "wrap" : "nowrap",
						paddingBottom: "2px",
						overflowX: "auto",
						overflowY: "visible",
					}}
				>
					{extrasToShow.map((extra, index) => (
						<Box
							key={index}
							onClick={() => {
								if (extra.pageId) {
									handleNavigate(extra.pageId);
								}
							}}
							sx={{
								backgroundColor: "#fff",
								borderRadius: "12px",
								width: isSmallScreen
									? "calc(33.333% - 6px)"
									: "calc(20% - 7px)",
								padding: isSmallScreen ? "0px 5px 2.5px 5px" : "10px 8px",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								textAlign: "center",
								justifyContent: "center",
								flexShrink: 0,
								minWidth: 60,
								cursor: extra.pageId ? "pointer" : "default",
								transition: "transform 0.3s ease, box-shadow 0.3s ease",
								//opacity: extra.pageId ? 1 : 0.7,
								"&:hover": extra.pageId
									? {
											transform: "translateY(-5px)",
											boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
										}
									: {},
							}}
						>
							{extra.image && !failedMenuImageUrls[extra.image] && (
								<Box
									sx={{
										fontSize: "24px",
										marginBottom: 1,
									}}
								>
									<Box
										component="img"
										src={extra.image}
										alt=""
										onError={() => markMenuImageFailed(extra.image)}
										sx={{
											width: 38,
											zIndex: 100,
										}}
									/>
								</Box>
							)}
							<Box
								sx={{
									fontSize: isSmallScreen
										? "10px !important"
										: "12px !important",
									fontWeight: 600,
									color: "#333",
									lineHeight: 1.3,
									marginTop: -2,
									fontFamily: `${
										fontFamily || "'Poppins', sans-serif"
									} !important`,
								}}
							>
								{extra.titulo}
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</Box>
	);
};

Template50.thumbnail = "/images/template-1.png";

export default Template50;
